import { fetchWithAuth } from './http-client';

import type {
  ListTeamParams,
  SaveTeamPayload,
  Team,
  TeamListResponse,
  UpdateTeamMembersPayload
} from '@/types/teams';

// Using versioned API endpoints (v1) for proper data transformations
const TEAM_API_BASE_URL = import.meta.env.VITE_TEAM_API_URL || '/api/v1/teams';
const TEAM_API_ENV_VAR = 'VITE_TEAM_API_URL';

function buildQuery(params: ListTeamParams = {}): string {
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.set('search', params.search);
  }

  if (params.includeMembers) {
    searchParams.set('includeMembers', 'true');
  }

  if (params.organizationUnitId) {
    searchParams.set('organizationUnitId', params.organizationUnitId);
  }

  if (typeof params.limit === 'number') {
    searchParams.set('limit', params.limit.toString());
  }

  if (typeof params.offset === 'number') {
    searchParams.set('offset', params.offset.toString());
  }

  if (params.sortBy) {
    searchParams.set('sortBy', params.sortBy);
  }

  if (params.sortOrder) {
    searchParams.set('sortOrder', params.sortOrder);
  }

  if (params.includeArchived) {
    searchParams.set('includeArchived', 'true');
  }

  return searchParams.toString();
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const method = init.method ?? 'GET';
  const response = await fetchWithAuth(`${TEAM_API_BASE_URL}${path}`, {
    ...init,
    method,
    context: `Solicitud de equipos (${method})`,
    envVar: TEAM_API_ENV_VAR,
    baseUrl: TEAM_API_BASE_URL,
  });

  if (!response.ok) {
    let message = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body) {
        if (typeof body.error === 'string') {
          message = body.error;
        } else if (body.error && typeof body.error === 'object') {
          const e = body.error as any;
          message = e.message || e.detail || (e.details && e.details.message) || e.code || message;
        } else if (typeof body.message === 'string') {
          message = body.message;
        }
      }
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response;
}

export async function listTeams(params: ListTeamParams = {}): Promise<TeamListResponse> {
  const query = buildQuery(params);
  const response = await send(query ? `?${query}` : '', { method: 'GET' });
  const payload = (await response.json()) as TeamListResponse;
  return {
    data: payload.data ?? [],
    meta: payload.meta ?? { total: payload.data?.length ?? 0, limit: params.limit ?? 0, offset: params.offset ?? 0 }
  };
}

export async function getTeam(teamId: string): Promise<Team> {
  const response = await send(`/${teamId}?includeMembers=true`, { method: 'GET' });
  const payload = (await response.json()) as { data?: Team };
  if (!payload.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return payload.data;
}

export async function createTeam(payload: SaveTeamPayload): Promise<Team> {
  const response = await send('', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  const body = (await response.json()) as { data?: Team };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

export async function updateTeam(teamId: string, payload: Partial<SaveTeamPayload>): Promise<Team> {
  const response = await send(`/${teamId}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
  const body = (await response.json()) as { data?: Team };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

export async function updateTeamMembers(teamId: string, payload: UpdateTeamMembersPayload): Promise<Team> {
  const response = await send(`/${teamId}/members`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  });
  const body = (await response.json()) as { data?: Team };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

export async function deleteTeam(teamId: string): Promise<void> {
  await send(`/${teamId}`, { method: 'DELETE' });
}

/**
 * Bulk delete multiple teams at once
 * @param ids - Array of team UUIDs to delete
 * @returns Result with deleted count and any failed IDs
 */
export async function bulkDeleteTeams(ids: string[]): Promise<{ deleted: number; failed: string[] }> {
  if (!ids || ids.length === 0) {
    return { deleted: 0, failed: [] };
  }

  const response = await send('/bulk', {
    method: 'DELETE',
    body: JSON.stringify({ ids })
  });

  const body = await response.json() as { data?: { deleted: number; failed: string[] } };
  return body.data ?? { deleted: 0, failed: ids };
}

/**
 * Archive team
 * @param teamId - Team ID to archive
 * @param reason - Optional reason for archiving
 * @returns Updated team record
 */
export async function archiveTeam(teamId: string, reason?: string): Promise<Team> {
  const response = await send(`/${teamId}/archive`, {
    method: 'PATCH',
    body: JSON.stringify({ reason })
  });
  const body = (await response.json()) as { data?: Team };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

/**
 * Unarchive team
 * @param teamId - Team ID to unarchive
 * @returns Updated team record
 */
export async function unarchiveTeam(teamId: string): Promise<Team> {
  const response = await send(`/${teamId}/unarchive`, {
    method: 'PATCH'
  });
  const body = (await response.json()) as { data?: Team };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}
